import { getInDomain } from '../../domains/externalUtils';
import * as ListenerUtils from '../../lib/ListenerUtils';
import { getFromMap } from '../CoreUtils';
import { getIsSearchOpen } from '../UIStateUtils';

import {
  createHelpUrl,
  getCurrentPathnameFromState,
  getNowPlayingPath,
  getStationPlayPathForStationCode,
  isOnDemandNowPlayingRoute,
  isPathInNowPlayingPath,
  searchRegex,
  STATION_BASE_PATH,
  stripTrailingSlash,
} from './shared';

export const pathsThatDoNotRedirectToNowPlayingIfInactive = [
  /^\/station$/i,
  /^\/station\/play\/\w+$/i,
  /^\/artist\/.+/i,
  /^\/account(\/?|\/.+)$/i,
  /^\/settings\/upgrade(\/?)$/i,
  /^\/upgrade.*$/i,
  /^\/payment.*$/i,
  /^\/podcast\/.+/i,
  /^\/verify.*$/i,
  /^\/subscription\/activate.*$/i,
  /^$/,
];

export function getBackstagePathForToken(token) {
  return token && `/artist/${token}`;
}

/**
 * Selector that returns the backstage path to the currently playing track, if any.
 *
 * @param {Map} track - the now playing track.
 * @returns {string} The known path or an empty string
 */
export function getTrackSongPath(track) {
  return getBackstagePathForToken(getFromMap(track, 'trackSeoToken'));
}

/**
 * Selector that returns the backstage path to the artist of the currently playing track, if any.
 *
 * @param {Map} track - the now playing track.
 * @returns {string} The known path or an empty string
 */
export function getTrackArtistPath(track) {
  return getBackstagePathForToken(getFromMap(track, 'artistSeoToken'));
}

/**
 * Selector that returns the backstage path to the album of the currently playing track, if any.
 *
 * @param {Map} track - the now playing track.
 * @returns {string} The known path or an empty string
 */
export function getTrackAlbumPath(track) {
  return getBackstagePathForToken(getFromMap(track, 'albumSeoToken'));
}

export function getStationDetailsPathForStationId(stationId) {
  return `${STATION_BASE_PATH}/${stationId}`;
}

export function isNowPlayingPlaylistRoute(path) {
  const ON_DEMAND_NP_PLAYLIST_PATH_REGEX = new RegExp(/(playlist)\/\play\//);
  return ON_DEMAND_NP_PLAYLIST_PATH_REGEX.test(path);
}

export function isCurrentRouteOnDemandNowPlaying(state) {
  const currentPath = getCurrentPathnameFromState(state);
  return currentPath && isOnDemandNowPlayingRoute(currentPath);
}

export function getMyStationsPath() {
  return '/stations';
}

export function isPathInMyStations(path) {
  return path === getMyStationsPath();
}

/**
 * Route to the now playing url for a particular station
 *
 * @param {object} router - the application router
 * @param {string} stationId  - stationId
 */
export function routeToStationPlay(router, stationId) {
  const pathToRouteTo = getStationPlayPathForStationCode(stationId);

  // only route if specified URL is not already active
  if (!router.isActive(pathToRouteTo, true)) {
    router.push(pathToRouteTo);
  }
}

export function getNowPlayingReroutePath(state, id) {
  // eslint-disable-next-line import/no-deprecated
  const isBasic = ListenerUtils.listenerIsBasic(state);
  // eslint-disable-next-line import/no-deprecated
  const isPlus = ListenerUtils.listenerIsPlus(state);
  // eslint-disable-next-line import/no-deprecated
  const isPremium = ListenerUtils.listenerIsPremium(state);
  let reroutePath;
  if (isBasic || isPlus) {
    reroutePath = getStationPlayPathForStationCode(id);
  } else if (isPremium) {
    const currentPath = getCurrentPathnameFromState(state);
    const isNowPlaying = isPathInNowPlayingPath(currentPath);
    reroutePath = isNowPlaying && getNowPlayingPath(state);
  }
  return reroutePath;
}
/**
 *
 * @param {object} state -
 * @param {string} id - id of currently playing station
 * @returns {reroutePath} path to now playing, returned only if the user isn't currently on now playing
 */
export function getNowPlayingPathIfNotCurrent(state) {
  let reroutePath;
  const currentPath = getCurrentPathnameFromState(state);
  const isNowPlaying = isPathInNowPlayingPath(currentPath);
  const isSearchOpen = getIsSearchOpen(state);

  // accounts for when the URL in state is Now Playing but the search overlay is open over it
  const shouldReroute = !isNowPlaying || isSearchOpen;
  const currentStationId = getInDomain('playback.utils.getCurrentStationId', null)(state);
  if (currentStationId) {
    reroutePath = getStationPlayPathForStationCode(currentStationId);
  } else {
    reroutePath = getNowPlayingPath(state);
  }
  return shouldReroute && reroutePath;
}

export function pathRedirectsToNowPlayingIfInactive(pathname) {
  // TODO: WEBCLIENT-6245 Remove this check and move both search regex into pathsThatDoNotRedirectToNowPlayingIfInactive array.
  if (!pathsThatDoNotRedirectToNowPlayingIfInactive.includes(searchRegex)) {
    pathsThatDoNotRedirectToNowPlayingIfInactive.push(/^\/search\/.*\/[a-zA-Z0-9_]*$/, searchRegex);
  }
  return !pathsThatDoNotRedirectToNowPlayingIfInactive.some(regex =>
    regex.test(stripTrailingSlash(pathname)),
  );
}

export function getFeaturedTrackHelpUrl() {
  return createHelpUrl('/s/article/Featured-Track-1519949297416');
}

export function isNowPlayingArtistRoute(path) {
  const ON_DEMAND_NP_PATH_REGEX = new RegExp(/(artist)\/\play\//);
  return ON_DEMAND_NP_PATH_REGEX.test(path);
}

export function isNowPlayingStationRoute(path) {
  const ON_DEMAND_NP_PLAYLIST_PATH_REGEX = new RegExp(/(station)\/\play\//);
  return ON_DEMAND_NP_PLAYLIST_PATH_REGEX.test(path);
}
